import React from 'react';
import { Weight, Activity } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { PetProfile } from '../../types';
import { useTranslation } from '../../hooks/useTranslation';

interface WeightChartProps {
  profile: PetProfile;
  chartData: Array<{ date: string; weight: number }>;
  stoolData: Array<{ date: string; score: number }>;
}

export const WeightChart: React.FC<WeightChartProps> = ({
  profile,
  chartData,
  stoolData
}) => {
  const { t } = useTranslation();

  if (!profile.weight && chartData.length === 0) {
    return (
      <div className="bg-white p-6 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-sm font-black text-gray-900 uppercase tracking-widest flex items-center gap-2">
            <Weight size={14}/> {t('app:dashboard.weight')}
          </h3>
        </div>
        <div className="text-center py-8">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white shadow-lg mx-auto mb-4 ${profile.type === 'chat' ? 'bg-orange-100 text-orange-500' : 'bg-indigo-100 text-indigo-500'}`}>
            <Weight size={32}/>
          </div>
          <p className="text-gray-400 text-sm">{t('app:dashboard.no_weight_data')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-[2.5rem] border border-gray-100 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-sm font-black text-gray-900 uppercase tracking-widest flex items-center gap-2">
          <Weight size={14}/> {t('app:dashboard.weight')}
        </h3>
        {profile.weight && (
          <div className="text-right">
            <p className="text-2xl font-black text-gray-900">{profile.weight}</p>
            <p className="text-[10px] text-gray-400 font-black uppercase">{t('app:dashboard.current_kg')}</p>
          </div>
        )}
      </div>
      
      {chartData.length > 0 && (
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 10 }} 
                stroke="#9ca3af"
                axisLine={false}
                tickLine={false}
              />
              <YAxis 
                tick={{ fontSize: 10 }} 
                stroke="#9ca3af"
                axisLine={false}
                tickLine={false}
                domain={['dataMin - 1', 'dataMax + 1']}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '12px',
                  fontSize: '12px'
                }}
              />
              <Line 
                type="monotone" 
                dataKey="weight" 
                stroke={profile.type === 'chat' ? '#f97316' : '#4f46e5'} 
                strokeWidth={2}
                dot={{ fill: profile.type === 'chat' ? '#f97316' : '#4f46e5', r: 3 }}
                activeDot={{ r: 5 }}
              />
              {profile.weight && (
                <ReferenceLine 
                  y={profile.weight} 
                  stroke={profile.type === 'chat' ? '#f97316' : '#4f46e5'} 
                  strokeDasharray="5 5" 
                  strokeWidth={1}
                  strokeOpacity={0.5}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      
      {stoolData.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <Activity size={14} className="text-orange-500"/>
            <p className="text-[10px] font-black text-gray-400 uppercase">{t('app:dashboard.stool_quality')}</p>
          </div>
          <div className="h-24">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stoolData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 10 }} 
                  stroke="#9ca3af"
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis 
                  tick={{ fontSize: 10 }} 
                  stroke="#9ca3af"
                  axisLine={false}
                  tickLine={false}
                  domain={[0, 7]}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '12px',
                    fontSize: '12px'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="score" 
                  stroke="#f97316" 
                  strokeWidth={2}
                  dot={{ fill: '#f97316', r: 3 }}
                  activeDot={{ r: 5 }}
                />
                <ReferenceLine y={4} stroke="#10b981" strokeWidth={1} strokeOpacity={0.5} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};
